/**
 * CR221: Publish Confirm Dialog with Unsaved Changes Guardrail
 * 
 * Prevents accidental publish when there are unsaved changes.
 * Shows clear options: "Save & Publish" vs "Publish Last Saved"
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  Save,
  Play,
  Eye,
  Clock,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { ActivationEnvironment } from "@/lib/layout-studio/types";

interface PublishConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  environment: ActivationEnvironment;
  presetName: string;
  hasUnsavedChanges: boolean;
  headerWarnings: { message: string; severity: "warning" | "error" }[];
  lastSavedAt?: Date | string | null;
  saving: boolean;
  onSaveAndPublish: () => Promise<void>;
  onPublishLastSaved: () => Promise<void>;
  onCancel: () => void;
}

export function PublishConfirmDialog({
  open,
  onOpenChange,
  environment,
  presetName,
  hasUnsavedChanges,
  headerWarnings,
  lastSavedAt,
  saving,
  onSaveAndPublish,
  onPublishLastSaved,
  onCancel,
}: PublishConfirmDialogProps) {
  const [headerWarningConfirmed, setHeaderWarningConfirmed] = useState(false);
  const [publishUnsavedConfirmed, setPublishUnsavedConfirmed] = useState(false);

  const isLive = environment === "publish";
  const envLabel = isLive ? "LIVE" : "Staging";
  
  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setHeaderWarningConfirmed(false);
      setPublishUnsavedConfirmed(false);
    }
    onOpenChange(newOpen);
  };

  const lastSavedDate = lastSavedAt 
    ? (typeof lastSavedAt === "string" ? new Date(lastSavedAt) : lastSavedAt)
    : null;

  const canPublish = headerWarnings.length === 0 || headerWarningConfirmed;
  const canPublishLastSaved = !hasUnsavedChanges || publishUnsavedConfirmed;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(isLive || headerWarnings.length > 0 || hasUnsavedChanges) && (
              <AlertTriangle className="h-5 w-5 text-warning" />
            )}
            Activate for {envLabel}?
          </DialogTitle>
          <DialogDescription>
            {isLive ? (
              <>
                This will make <strong>{presetName}</strong> the active layout for the{" "}
                <strong>public site</strong>. All visitors will see this layout immediately.
              </>
            ) : (
              <>
                This will make <strong>{presetName}</strong> the active layout for{" "}
                <strong>staging/admin preview</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Unsaved Changes Warning */}
          {hasUnsavedChanges && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium">You have unsaved changes</div>
                <div className="text-sm mt-1">
                  Choose whether to save your changes before publishing, or publish the last saved version.
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* What will be published */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="text-sm font-medium">What will be published:</div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Preset</span>
              <Badge variant="outline">{presetName}</Badge>
            </div>
            {lastSavedDate && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last saved</span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(lastSavedDate, { addSuffix: true })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Target</span>
              <Badge variant={isLive ? "default" : "secondary"}>{envLabel}</Badge>
            </div>
          </div>

          {/* Header Validation Warnings */}
          {headerWarnings.length > 0 && (
            <div className="space-y-3">
              {headerWarnings.map((warning, i) => (
                <Alert key={i} variant={warning.severity === "error" ? "destructive" : "default"}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    {warning.message}
                  </AlertDescription>
                </Alert>
              ))}
              
              <div className="flex items-center space-x-2 pt-2 border-t">
                <Checkbox
                  id="confirm-header-warning"
                  checked={headerWarningConfirmed}
                  onCheckedChange={(checked) => setHeaderWarningConfirmed(checked === true)}
                />
                <Label
                  htmlFor="confirm-header-warning"
                  className="text-sm font-medium leading-none"
                >
                  I understand the header may not display correctly
                </Label>
              </div>
            </div>
          )}
        </div>

        <Separator />

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          
          {hasUnsavedChanges ? (
            <>
              {/* Publish Last Saved option */}
              <div className="flex items-center gap-2">
                {!publishUnsavedConfirmed && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="confirm-publish-unsaved"
                      checked={publishUnsavedConfirmed}
                      onCheckedChange={(checked) => setPublishUnsavedConfirmed(checked === true)}
                    />
                    <Label htmlFor="confirm-publish-unsaved" className="text-xs">
                      Discard changes
                    </Label>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={onPublishLastSaved}
                  disabled={saving || !canPublish || !canPublishLastSaved}
                >
                  <Play className="h-4 w-4 mr-1.5" />
                  Publish Last Saved
                </Button>
              </div>
              
              {/* Save & Publish (primary) */}
              <Button
                variant={isLive ? "destructive" : "default"}
                onClick={onSaveAndPublish}
                disabled={saving || !canPublish}
              >
                <Save className="h-4 w-4 mr-1.5" />
                {saving ? "Publishing..." : "Save & Publish"}
              </Button>
            </>
          ) : (
            /* Standard publish */
            <Button
              variant={isLive ? "destructive" : "default"}
              onClick={onPublishLastSaved}
              disabled={saving || !canPublish}
            >
              {isLive ? <Play className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
              {saving ? "Activating..." : `Activate for ${envLabel}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
